import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { Textarea } from '../../../components/ui/Textarea';
import { Label } from '../../../components/ui/Label';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Search, Eye,
  Download, Calendar, Clock, User, FileText,
  BarChart3, Plus, Trash2, X
} from 'lucide-react';
import toast from 'react-hot-toast';
import MockDataService from '../../../services/MockDataService';

const AuditFindings = () => {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedFinding, setSelectedFinding] = useState(null);
  const [selectedStatsModal, setSelectedStatsModal] = useState(null);
  const [newFinding, setNewFinding] = useState({
    title: '',
    description: '',
    severity: 'medium',
    category: 'security',
    status: 'open',
    assignedTo: '',
    dueDate: '',
    remediation: ''
  });

  useEffect(() => {
    loadFindings();
  }, []);

  const loadFindings = async () => {
    try {
      setLoading(true);
      const response = await MockDataService.getAuditFindings();
      if (response.success) {
        setFindings(response.data);
      }
    } catch (error) {
      console.error('Error loading audit findings:', error);
      toast.error('Failed to load findings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFinding = async () => {
    // Validation
    if (!newFinding.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (!newFinding.description.trim()) {
      toast.error('Please enter a description');
      return;
    }

    try {
      toast.loading('Creating finding...', { id: 'create-finding' });
      const response = await MockDataService.createAuditFinding(newFinding);
      if (response.success) {
        setFindings([...findings, response.data]);
        setShowCreateModal(false);
        setNewFinding({
          title: '',
          description: '',
          severity: 'medium',
          category: 'security',
          status: 'open',
          assignedTo: '',
          dueDate: '',
          remediation: ''
        });
        toast.success('Finding created successfully', { id: 'create-finding' });
      }
    } catch (error) {
      console.error('Error creating audit finding:', error);
      toast.error('Failed to create finding', { id: 'create-finding' });
    }
  };

  const handleViewFinding = (finding) => {
    setSelectedFinding(finding);
    setShowViewModal(true);
  };

  const handleResolveFinding = async (findingId) => {
    const finding = findings.find(f => f.id === findingId);
    if (!finding) return;

    try {
      toast.loading('Resolving finding...', { id: `resolve-${findingId}` });
      const response = await MockDataService.resolveAuditFinding(findingId);
      if (response.success) {
        const updatedFindings = findings.map(f => 
          f.id === findingId 
            ? { ...f, status: 'resolved', resolvedAt: new Date().toISOString() }
            : f
        );
        setFindings(updatedFindings);
        toast.success(`"${finding.title}" marked as resolved`, { id: `resolve-${findingId}` });
      }
    } catch (error) {
      console.error('Error resolving audit finding:', error);
      toast.error('Failed to resolve finding', { id: `resolve-${findingId}` });
    }
  };

  const handleDeleteFinding = async (findingId) => {
    const finding = findings.find(f => f.id === findingId);
    if (!finding) return;

    if (!window.confirm(`Are you sure you want to delete "${finding.title}"?`)) {
      return;
    }

    try {
      toast.loading('Deleting finding...', { id: `delete-${findingId}` });
      const updatedFindings = findings.filter(f => f.id !== findingId);
      setFindings(updatedFindings);
      toast.success('Finding deleted successfully', { id: `delete-${findingId}` });
    } catch (error) {
      console.error('Error deleting audit finding:', error);
      toast.error('Failed to delete finding', { id: `delete-${findingId}` });
    }
  };

  const handleExportFindings = () => {
    toast.loading('Exporting findings...', { duration: 1500 });
    setTimeout(() => {
      toast.success(`Exporting ${filteredFindings.length} findings to CSV...`);
      //console.log('Exporting findings:', filteredFindings);
      // Simulate CSV export
      const csvContent = generateCSV(filteredFindings);
      //console.log('CSV Content:', csvContent);
    }, 1500);
  };

  const handleExportSingleFinding = (finding) => {
    toast.success(`Exporting "${finding.title}"...`);
    //console.log('Exporting finding:', finding);
    setShowViewModal(false);
  };

  const generateCSV = (data) => {
    const headers = ['ID', 'Title', 'Severity', 'Status', 'Assigned To', 'Due Date', 'Risk Score'];
    const rows = data.map(f => [
      f.id,
      f.title,
      f.severity,
      f.status,
      f.assignedTo,
      new Date(f.dueDate).toLocaleDateString(),
      f.riskScore
    ]);
    return [headers, ...rows].map(row => row.join(',')).join('\n');
  };

  const handleStatsCardClick = (type) => {
    setSelectedStatsModal(type);
  };

  const getStatsModalData = () => {
    switch (selectedStatsModal) {
      case 'total':
        return { title: 'All Findings', findings: findings };
      case 'open':
        return { title: 'Open Findings', findings: findings.filter(f => f.status === 'open') };
      case 'in_progress':
        return { title: 'In Progress Findings', findings: findings.filter(f => f.status === 'in_progress') };
      case 'resolved':
        return { title: 'Resolved Findings', findings: findings.filter(f => f.status === 'resolved') };
      default:
        return null;
    }
  };

  const filteredFindings = findings.filter(finding => {
    const matchesSearch = finding.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         finding.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSeverity = filterSeverity === 'all' || finding.severity === filterSeverity;
    const matchesStatus = filterStatus === 'all' || finding.status === filterStatus;
    
    return matchesSearch && matchesSeverity && matchesStatus;
  });

  const getSeverityBadge = (severity) => {
    const severityConfig = {
      critical: { 
        bgColor: 'bg-[#BB0000]', 
        textColor: 'text-white',
        icon: AlertTriangle 
      },
      high: { 
        bgColor: 'bg-[#BB0000]', 
        textColor: 'text-white',
        icon: AlertTriangle 
      },
      medium: { 
        bgColor: 'bg-[#FFE7E0]', 
        textColor: 'text-[#BB0000]',
        icon: AlertTriangle 
      },
      low: { 
        bgColor: 'bg-[#006600]', 
        textColor: 'text-white',
        icon: CheckCircle 
      }
    };
    
    const config = severityConfig[severity] || severityConfig.medium;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon size={12} />
        {severity}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      open: { 
        bgColor: 'bg-[#BB0000]', 
        textColor: 'text-white',
        icon: XCircle 
      },
      in_progress: { 
        bgColor: 'bg-[#0B2530]', 
        textColor: 'text-white',
        icon: Clock 
      },
      resolved: { 
        bgColor: 'bg-[#006600]', 
        textColor: 'text-white',
        icon: CheckCircle 
      }
    };
    
    const config = statusConfig[status] || statusConfig.open;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon size={12} />
        {status.replace('_', ' ')}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: '#006600' }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#0B2530' }}>Audit Findings</h1>
          <p style={{ color: '#4A5B63' }}>Manage security audit findings and compliance issues</p>
        </div>
        <Button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2"
        >
          <Plus size={16} />
          Add Finding
        </Button>
      </div>

      {/* Stats Cards - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => handleStatsCardClick('total')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Findings</CardTitle>
            <Shield className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{findings.length}</div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-[#BB0000]"
          onClick={() => handleStatsCardClick('open')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open Findings</CardTitle>
            <XCircle className="h-4 w-4" style={{ color: '#BB0000' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#BB0000' }}>
              {findings.filter(f => f.status === 'open').length}
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-[#0B2530]"
          onClick={() => handleStatsCardClick('in_progress')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Clock className="h-4 w-4" style={{ color: '#0B2530' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#0B2530' }}>
              {findings.filter(f => f.status === 'in_progress').length}
            </div>
          </CardContent>
        </Card>
        
        <Card 
          className="cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-[#006600]"
          onClick={() => handleStatsCardClick('resolved')}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Resolved</CardTitle>
            <CheckCircle className="h-4 w-4" style={{ color: '#006600' }} />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" style={{ color: '#006600' }}>
              {findings.filter(f => f.status === 'resolved').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2" size={16} style={{ color: '#8A9AA2' }} />
            <Input
              placeholder="Search findings..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterSeverity} onValueChange={setFilterSeverity}>
            <SelectTrigger>
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger>
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
            </SelectContent>
          </Select>
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={handleExportFindings}
            title="Export filtered findings to CSV"
          >
            <Download size={16} />
            Export ({filteredFindings.length})
          </Button>
        </div>
      </Card>

      {/* Findings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredFindings.map((finding) => (
          <Card key={finding.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{finding.title}</CardTitle>
                <div className="flex gap-2">
                  {getSeverityBadge(finding.severity)}
                  {getStatusBadge(finding.status)}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">{finding.description}</p>
              
              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User size={14} />
                  <span>Assigned: {finding.assignedTo}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar size={14} />
                  <span>Due: {new Date(finding.dueDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <BarChart3 size={14} />
                  <span>Risk Score: {finding.riskScore}/10</span>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewFinding(finding)}
                  className="flex-1"
                  title="View details"
                >
                  <Eye size={14} className="mr-1" />
                  View
                </Button>
                {finding.status !== 'resolved' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleResolveFinding(finding.id)}
                    className="flex-1 border-[#006600] text-[#006600] hover:bg-[#E8FCF7]"
                    title="Mark as resolved"
                  >
                    <CheckCircle size={14} className="mr-1" />
                    Resolve
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDeleteFinding(finding.id)}
                  className="text-red-600 hover:text-red-700"
                  title="Delete finding"
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Finding Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowCreateModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: '#E5EFF3' }}>
              <h2 className="text-lg font-semibold" style={{ color: '#0B2530' }}>Add Audit Finding</h2>
              <button 
                onClick={() => setShowCreateModal(false)}
                className="p-2 hover:bg-[#F8FCFB] rounded-full transition-colors"
              >
                <X size={20} style={{ color: '#8A9AA2' }} />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={newFinding.title}
                  onChange={(e) => setNewFinding({ ...newFinding, title: e.target.value })}
                  placeholder="Enter finding title"
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={newFinding.description}
                  onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })}
                  placeholder="Enter finding description"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="severity">Severity</Label>
                  <Select value={newFinding.severity} onValueChange={(value) => setNewFinding({ ...newFinding, severity: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="category">Category</Label>
                  <Select value={newFinding.category} onValueChange={(value) => setNewFinding({ ...newFinding, category: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="security">Security</SelectItem>
                      <SelectItem value="data">Data</SelectItem>
                      <SelectItem value="access">Access</SelectItem>
                      <SelectItem value="compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="assignedTo">Assigned To</Label>
                <Input
                  id="assignedTo"
                  value={newFinding.assignedTo}
                  onChange={(e) => setNewFinding({ ...newFinding, assignedTo: e.target.value })}
                  placeholder="Enter assignee"
                />
              </div>

              <div>
                <Label htmlFor="dueDate">Due Date</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={newFinding.dueDate}
                  onChange={(e) => setNewFinding({ ...newFinding, dueDate: e.target.value })}
                />
              </div>

              <div>
                <Label htmlFor="remediation">Remediation</Label>
                <Textarea
                  id="remediation"
                  value={newFinding.remediation}
                  onChange={(e) => setNewFinding({ ...newFinding, remediation: e.target.value })}
                  placeholder="Enter remediation steps"
                  rows={3}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateFinding}>
                  Add Finding
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Finding Modal */}
      {showViewModal && selectedFinding && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setShowViewModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4">
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: '#E5EFF3' }}>
              <h2 className="text-lg font-semibold" style={{ color: '#0B2530' }}>{selectedFinding.title}</h2>
              <button 
                onClick={() => setShowViewModal(false)}
                className="p-2 hover:bg-[#F8FCFB] rounded-full transition-colors"
              >
                <X size={20} style={{ color: '#8A9AA2' }} />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Severity</Label>
                  <div className="mt-2">
                    {getSeverityBadge(selectedFinding.severity)}
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-2">
                    {getStatusBadge(selectedFinding.status)}
                  </div>
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <p style={{ color: '#0B2530' }}>{selectedFinding.description}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Assigned To</Label>
                  <p style={{ color: '#0B2530' }}>{selectedFinding.assignedTo}</p>
                </div>
                <div>
                  <Label>Due Date</Label>
                  <p style={{ color: '#0B2530' }}>{new Date(selectedFinding.dueDate).toLocaleDateString()}</p>
                </div>
              </div>

              <div>
                <Label>Risk Score</Label>
                <p style={{ color: '#0B2530' }}>{selectedFinding.riskScore}/10</p>
              </div>

              <div>
                <Label>Remediation</Label>
                <p style={{ color: '#0B2530' }}>{selectedFinding.remediation}</p>
              </div>

              <div>
                <Label>Evidence</Label>
                <div className="space-y-2">
                  {selectedFinding.evidence.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <FileText size={14} />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowViewModal(false)}>
                  Close
                </Button>
                <Button 
                  className="flex items-center gap-2"
                  onClick={() => handleExportSingleFinding(selectedFinding)}
                >
                  <Download size={16} />
                  Export Finding
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {selectedStatsModal && (() => {
        const modalData = getStatsModalData();
        return modalData && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
            onClick={() => setSelectedStatsModal(null)}
          >
            <div 
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-border dark:border-gray-700 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-foreground dark:text-white">
                  {modalData.title}
                </h2>
                <button 
                  onClick={() => setSelectedStatsModal(null)}
                  className="text-muted-foreground hover:text-foreground dark:hover:text-gray-300"
                >
                  <X size={24} />
                </button>
              </div>
              
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
                {modalData.findings.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    No findings found in this category.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {modalData.findings.map((finding) => (
                      <Card key={finding.id} className="p-4 hover:shadow-lg transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <h3 className="font-semibold text-foreground dark:text-white flex-1">
                            {finding.title}
                          </h3>
                          <div className="flex gap-2">
                            {getSeverityBadge(finding.severity)}
                            {getStatusBadge(finding.status)}
                          </div>
                        </div>
                        
                        <p className="text-sm text-muted-foreground dark:text-gray-400 mb-3 line-clamp-2">
                          {finding.description}
                        </p>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground dark:text-gray-400">Risk Score:</span>
                            <span className="font-semibold">{finding.riskScore}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground dark:text-gray-400">Assigned To:</span>
                            <span className="font-semibold">{finding.assignedTo}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-muted-foreground dark:text-gray-400">Due Date:</span>
                            <span className="font-semibold">
                              {new Date(finding.dueDate).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex gap-2 mt-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setSelectedStatsModal(null);
                              setSelectedFinding(finding);
                              setShowViewModal(true);
                            }}
                            className="flex-1"
                          >
                            <Eye size={14} className="mr-1" />
                            View
                          </Button>
                          {finding.status !== 'resolved' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => {
                                handleResolveFinding(finding.id);
                                setSelectedStatsModal(null);
                              }}
                              className="flex-1"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Resolve
                            </Button>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="p-6 border-t border-border dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-muted-foreground dark:text-gray-400">
                  Total: {modalData.findings.length} finding(s)
                </div>
                <Button 
                  variant="outline"
                  onClick={() => setSelectedStatsModal(null)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AuditFindings;
